import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LockClosedIcon } from "@heroicons/react/outline";

/**
 * PeriodLockGate — replaces a form body when the closed period leaves no valid
 * date to write at all.
 *
 * "Disable, don't hide" is the rule everywhere else: a disabled submit plus a
 * reason teaches, a missing button teaches nothing. This is the exception. An
 * opening balance is, by construction, dated *before* any closed period, so once
 * the books are closed past it there is no date the server would accept —
 * rendering a form that is guaranteed to fail is not honest, and constraining
 * the picker instead would leave a calendar with nothing selectable.
 *
 * Structurally mirrors WriteBlockGate so the two locked states read as one idea.
 * `locked` is computed by the caller because the condition differs per surface.
 */
const PeriodLockGate = ({
  locked,
  closedThroughLabel,
  manageTo,
  children,
  className = "",
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  if (!locked) return <>{children}</>;

  const target =
    manageTo ||
    (location.pathname.startsWith("/v2/")
      ? "/v2/accounting/period"
      : "/period-close");

  return (
    <div className={`px-4 py-10 ${className}`}>
      <div className="mx-auto w-full max-w-sm text-center">
        {/* One warm accent — signals "closed", not "broken". */}
        <div className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50">
          <LockClosedIcon
            className="h-5 w-5 text-amber-500"
            strokeWidth={1.5}
            aria-hidden="true"
          />
        </div>

        <h3 className="text-base font-semibold tracking-tight text-gray-900">
          {t("accounting.period.gate.title")}
        </h3>
        <p className="mx-auto mt-1.5 max-w-xs text-sm leading-relaxed text-gray-500">
          {t("accounting.period.gate.description", {
            date: closedThroughLabel,
          })}
        </p>

        <Link
          to={target}
          className="mt-6 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {t("accounting.period.gate.cta")}
        </Link>

        {/* Reassure: reading still works — only writing is closed. */}
        <p className="mt-3 text-xs text-gray-400">
          {t("accounting.period.gate.hint")}
        </p>
      </div>
    </div>
  );
};

export default PeriodLockGate;
